import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createCampaignContest, fetchAllCampaigns } from '../../api/campaigns';
import { fetchCompTeams } from '../../api/userTeams';
import { getConstraintTypes } from '../../api/constraints';
import { fetchCardVariations } from '../../api/cardVariation';
import { fetchAllRewards } from '../../api/rewards';
import { toast } from 'react-toastify';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { filterTempTeams } from '../../helpers/teamHelpers';
import ConstraintEditor from '../../components/admin/ConstraintEditor';

const formatRewardLabel = (r) => {
    const parts = [];
    if (r.money) parts.push(`$${r.money}`);
    if (r.numberOfSeries) parts.push(`${r.numberOfSeries} packs`);
    if (r.Card) parts.push(`${r.Card.year} ${r.Card.name}`);
    else if (r.cardID) parts.push(`Card #${r.cardID}`);
    if (r.description) parts.push(r.description);
    return parts.length > 0 ? parts.join(' + ') : `Reward #${r.rewardID}`;
};

const CreateCampaignContestPage = () => {
    const [searchParams] = useSearchParams();
    const [currentUser] = useAtom(currentUserAtom);
    const [campaignID, setCampaignID] = useState(searchParams.get('campaignID') || '');
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [rewardID, setRewardID] = useState('');
    const [userTeamID, setUserTeamID] = useState('');
    const [numOfGames, setNumOfGames] = useState('');
    const [maxRewards, setMaxRewards] = useState('');
    const [campaigns, setCampaigns] = useState([]);
    const [userTeams, setUserTeams] = useState([]);
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [constraintTypeDefs, setConstraintTypeDefs] = useState([]);
    const [constraints, setConstraints] = useState([]);
    const [variations, setVariations] = useState([]);
    const [variationID, setVariationID] = useState('');

    useEffect(() => {
        if (!currentUser) return;
        const fetchData = async () => {
            try {
                const [campaignData, userTeamsData, variationData, rewardData] = await Promise.all([
                    fetchAllCampaigns(),
                    fetchCompTeams(),
                    fetchCardVariations(),
                    fetchAllRewards(),
                ]);
                setCampaigns(campaignData);
                setUserTeams(filterTempTeams(userTeamsData));
                setVariations(
                    variationData
                        .filter(v => v.cardsPerHit)
                        .sort((a, b) => (a.cardsPerHit || 0) - (b.cardsPerHit || 0))
                );
                setRewards(rewardData);
            } catch (err) {
                toast.error('Error fetching data: ' + err.message);
            }
        };
        fetchData();
        getConstraintTypes()
            .then(setConstraintTypeDefs)
            .catch(err => console.error('Failed to fetch constraint types:', err));
    }, [currentUser]);

    const handleSubmit = async event => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                campaignID,
                name,
                desc,
                rewardID: rewardID || null,
                variationID: variationID || null,
                userTeamID,
                numOfGames,
                maxRewards: maxRewards ? parseInt(maxRewards, 10) : null,
                constraints,
            };
            await createCampaignContest(payload);
            toast.success('Campaign Contest created successfully!');
        } catch (err) {
            setError('Error creating campaign contest: ' + err.message);
            toast.error('Error creating campaign contest: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-page create-campaign-contest-page">
            <h2>Create Campaign Contest</h2>
            {error && <p className="error">{error}</p>}
            <form className="admin-form admin-form--vertical" onSubmit={handleSubmit}>
                <div className="admin-field">
                    <label htmlFor="campaignID">Campaign</label>
                    <select id="campaignID" value={campaignID} onChange={e => setCampaignID(e.target.value)} required>
                        <option value="">Select campaign</option>
                        {[...campaigns].sort((a, b) => a.name.localeCompare(b.name)).map(campaign => (
                            <option key={campaign.campaignID} value={campaign.campaignID}>
                                {campaign.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="admin-field">
                    <label htmlFor="name">Name</label>
                    <input id="name" type="text" value={name} onChange={e => setName(e.target.value)} />
                </div>

                <div className="admin-field">
                    <label htmlFor="desc">Description</label>
                    <textarea id="desc" value={desc} onChange={e => setDesc(e.target.value)} />
                </div>

                <div className="admin-form__row">
                    <div className="admin-field">
                        <label htmlFor="userTeamID">Comp Team</label>
                        <select id="userTeamID" value={userTeamID} onChange={e => setUserTeamID(e.target.value)} required>
                            <option value="">Select comp team</option>
                            {userTeams.map(team => (
                                <option key={team.userTeamID} value={team.userTeamID}>
                                    {team.name} ({team.score || 0})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="admin-field">
                        <label htmlFor="numOfGames">Number of Games</label>
                        <input id="numOfGames" type="number" value={numOfGames} onChange={e => setNumOfGames(e.target.value)} />
                    </div>
                </div>

                <fieldset className="admin-fieldset">
                    <legend>Reward</legend>
                    <div className="admin-field">
                        <label htmlFor="rewardID">Reward</label>
                        <select id="rewardID" value={rewardID} onChange={e => setRewardID(e.target.value)}>
                            <option value="">No reward</option>
                            {rewards.map(r => (
                                <option key={r.rewardID} value={r.rewardID}>
                                    {formatRewardLabel(r)}
                                </option>
                            ))}
                        </select>
                        <a href="/rewards/create" target="_blank" rel="noopener noreferrer" className="admin-field__helper-link">+ Create New Reward</a>
                    </div>
                    <div className="admin-form__row">
                        <div className="admin-field">
                            <label htmlFor="variationID">Reward Variation</label>
                            <select id="variationID" value={variationID} onChange={e => setVariationID(e.target.value)}>
                                <option value="">None (base card)</option>
                                {variations.map(v => (
                                    <option key={v.variationID} value={v.variationID}>
                                        {v.name} (1 in {v.cardsPerHit})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label htmlFor="maxRewards">Max Rewards</label>
                            <input id="maxRewards" type="number" min="1" value={maxRewards} onChange={e => setMaxRewards(e.target.value)} placeholder="Unlimited" />
                        </div>
                    </div>
                </fieldset>

                <ConstraintEditor
                    constraints={constraints}
                    onChange={setConstraints}
                    constraintTypeDefs={constraintTypeDefs}
                />

                <button type="submit" disabled={loading}>
                    {loading ? 'Creating...' : 'Create Campaign Contest'}
                </button>
            </form>
        </div>
    );
};

export default CreateCampaignContestPage;
